import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UNIT_MODELS } from "@/content";
import { Home, Layers3, Maximize2 } from "lucide-react";
import { WhatsAppGlyph, whatsappLink } from "@/components/WhatsApp";

export default function UnitModels() {
  const [active, setActive] = useState(0);
  const unit = UNIT_MODELS[active];

  const scrollToForm = () => {
    document.getElementById("form")?.scrollIntoView({ behavior: "smooth" });
    // Pre-select unit model
    setTimeout(() => {
      const ev = new CustomEvent("muyassr:select-unit", { detail: unit.code });
      window.dispatchEvent(ev);
    }, 400);
  };

  return (
    <section
      id="models"
      data-testid="section-models"
      className="relative py-24 lg:py-36 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto mb-12 lg:mb-16 text-center"
        >
          <span
            className="block text-xs text-brand-accent mb-5"
            data-testid="models-kicker"
          >
            نماذج الوحدات
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl text-brand-ink mb-6 leading-tight"
            data-testid="models-headline"
          >
            ستة تصاميم باختيارات واسعة
          </h2>
          <p className="text-base lg:text-lg text-brand-mute leading-loose">
            تشكيلة من نماذج الدوبلكس بمساحات وتوزيعات مدروسة تلبي تطلعات العائلة العصرية.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="models-tabs">
          {UNIT_MODELS.map((m, i) => (
            <button
              key={m.code}
              onClick={() => setActive(i)}
              className={`px-6 py-3 text-sm font-medium border transition-all duration-300 ${
                active === i
                  ? "bg-brand-ink text-brand-cream border-brand-ink"
                  : "bg-transparent text-brand-ink border-brand-border hover:border-brand-olive hover:text-brand-olive"
              }`}
              data-testid={`models-tab-${m.code}`}
            >
              نموذج {m.code}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={unit.code}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-12 gap-10 lg:gap-14 text-center lg:text-right"
          >
            {/* Image */}
            <div className="lg:col-span-7 bg-white border border-brand-border p-4 lg:p-6 rounded-2xl">
              <img
                src={unit.image}
                alt={unit.title}
                className="w-full h-auto object-contain max-h-[560px]"
                loading="lazy"
                data-testid={`models-image-${unit.code}`}
              />
            </div>

            {/* Details */}
            <div className="lg:col-span-5">
              <div className="flex items-center justify-center lg:justify-start gap-3 text-brand-accent mb-3">
                <span className="text-7xl font-serif leading-none">{unit.code}</span>
                <div className="h-px w-12 bg-brand-accent" />
              </div>
              <h3 className="text-2xl lg:text-3xl text-brand-ink mb-2 font-serif" data-testid={`models-title-${unit.code}`}>
                {unit.title}
              </h3>
              <div className="text-sm text-brand-mute mb-8" data-testid={`models-units-${unit.code}`}>
                الوحدات: {unit.units}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-8">
                <div className="border border-brand-border p-4 rounded-xl">
                  <div className="text-xs text-brand-mute mb-1 flex items-center gap-2">
                    <Maximize2 size={14} className="text-brand-accent" />
                    المساحة الإجمالية
                  </div>
                  <div className="text-lg text-brand-ink font-medium" data-testid={`models-area-${unit.code}`}>
                    {unit.area}
                  </div>
                </div>
                <div className="border border-brand-border p-4 rounded-xl">
                  <div className="text-xs text-brand-mute mb-1 flex items-center gap-2">
                    <Layers3 size={14} className="text-brand-accent" />
                    عدد الأدوار
                  </div>
                  <div className="text-lg text-brand-ink font-medium">{unit.floors.length}</div>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {unit.floors.map((f, i) => (
                  <div key={i} className="border-b border-brand-border pb-4" data-testid={`models-floor-${i}`}>
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-base text-brand-ink font-medium">{f.name}</span>
                      <span className="text-sm text-brand-accent font-medium">
                        {f.area}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {f.features.map((feat, k) => (
                        <span
                          key={k}
                          className="text-xs px-2.5 py-1 bg-brand-olive/8 text-brand-olive border border-brand-olive/20 rounded-sm"
                        >
                          {feat}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center gap-2 bg-brand-olive text-brand-cream px-6 py-3 text-sm font-medium hover:bg-brand-olive-dark transition-colors"
                  data-testid={`models-cta-${unit.code}`}
                >
                  <Home size={16} />
                  استفسر عن نموذج {unit.code}
                </button>
                <a
                  href={whatsappLink(unit.code, `unit-${unit.code}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 text-sm font-medium hover:bg-[#1eb958] transition-colors"
                  data-testid={`models-whatsapp-${unit.code}`}
                >
                  <WhatsAppGlyph size={14} />
                  واتساب
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

